import { OrderSearchRequest, OrderSearchResponse, OrderSearchResult, PlayerAutocompleteItem } from '@/api/models';
import { OrdersDB } from '@/db/repositories';
import dayjs from 'dayjs';

const CATEGORY_BY_TYPE = { club: 'single', component: 'batch', putter: 'putter' };
const DEFAULT_ROWS_PER_PAGE = 50;

// Cached orders identify club types through description markers.
const CLUB_DESCRIPTION_BY_HEAD_TYPE: Partial<Record<number, string>> = {
  1: 'D-',
  2: 'F-',
  3: 'H-',
  4: 'I-',
  5: 'W-',
  7: 'U-',
  30: 'Comp-Misc',
};

const matchesSelectedId = (selectedIds: number[] | undefined, value: number): boolean => {
  return selectedIds === undefined || selectedIds.includes(value);
};

export type OfflinePlayerSuggestion = Pick<PlayerAutocompleteItem, 'playerId' | 'fullName'> & { firstName: string; lastName: string };

export const getPlayerSuggestions = async (): Promise<OfflinePlayerSuggestion[]> => {
  const cachedResponse = await OrdersDB.getOfflineCache();
  const players = new Map<number, OfflinePlayerSuggestion>();

  for (const order of cachedResponse?.results ?? []) {
    if (players.has(order.playerId)) continue;
    const firstName = order.firstName ?? '';
    const lastName = order.lastName ?? '';
    const playerName = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (!playerName) continue;
    players.set(order.playerId, { playerId: order.playerId, firstName, lastName, fullName: playerName });
  }

  return [...players.values()].sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
};

export const searchOrders = async (request: OrderSearchRequest): Promise<OrderSearchResponse> => {
  const cachedResponse = await OrdersDB.getOfflineCache();

  if (!cachedResponse) {
    return {
      searchRowCount: 0,
      readyToShipOrderCount: null,
      results: [],
    };
  }

  const playerName = request.playerName?.trim().toLowerCase();
  const orderNo = request.orderNo?.trim().toLowerCase();
  const laserNo = request.laserNo?.trim().toLowerCase();

  const filteredOrders = cachedResponse.results.filter((order) => {
    //head type filter
    const matchesClubType =
      request.headTypeIds === undefined ||
      request.headTypeIds.some((headTypeId) => {
        const description = CLUB_DESCRIPTION_BY_HEAD_TYPE[headTypeId];
        return description !== undefined && order.clubDescriptions.includes(description);
      });
    //filter by Order Date (order.createDate) From and Date To if they exist
    const matchesDateRange =
      request.orderDateFrom === undefined ||
      (!dayjs(order.createDate).isBefore(request.orderDateFrom, 'day') && (request.orderDateTo === undefined || !dayjs(order.createDate).isAfter(request.orderDateTo, 'day')));
    //the basic filters
    const matches = {
      category: (request.orderTypes ?? ['club']).some((type) => CATEGORY_BY_TYPE[type] === order.orderCategoryShortName?.toLowerCase()),
      inventoryLocation: matchesSelectedId(request.inventoryLocationIds, order.inventoryLocationId),
      inHandFrom: !request.inHandDateFrom || Boolean(order.needInHandDate && order.needInHandDate.slice(0, 10) >= request.inHandDateFrom),
      inHandTo: !request.inHandDateTo || Boolean(order.needInHandDate && order.needInHandDate.slice(0, 10) <= request.inHandDateTo),
      clubType: matchesClubType,
      status: matchesSelectedId(request.orderStatusIds, order.orderStatusId),
      buildLocation: matchesSelectedId(request.buildLocationIds, order.buildLocationId),
      orderNumber: !orderNo || order.orderNo.toLowerCase().includes(orderNo),
      serialNumber: !laserNo || order.items.some((item) => item.laserNo?.toLowerCase().includes(laserNo)),
      account: matchesSelectedId(request.orderAccountIds, order.orderAccountId),
      playerName: !playerName || (order.firstName ?? '').toLowerCase().includes(playerName) || (order.lastName ?? '').toLowerCase().includes(playerName),
      player: !request.playerId || request.playerId === order.playerId,
      tourRep: matchesSelectedId(request.appUserIds, order.createAppUserId),
      dateRange: matchesDateRange,
    };

    return Object.values(matches).every(Boolean);
  });

  if (request.sortBy) {
    const { column, direction } = request.sortBy;
    const dateColumns = ['orderDate', 'needInHandDate', 'shipDate'];
    const valueFor = (order: OrderSearchResult): string | number | null => {
      if (column === 'playerName') return `${order.firstName ?? ''} ${order.lastName ?? ''}`.trim();
      const value = order[column];
      if (dateColumns.includes(column)) {
        if (!value) return null;
        const timestamp = Date.parse(value);
        return Number.isNaN(timestamp) ? null : timestamp;
      }
      return value ?? null;
    };
    filteredOrders.sort((left, right) => {
      const a = valueFor(left);
      const b = valueFor(right);
      let comparison = 0;
      if (a === null && b !== null) comparison = -1;
      else if (a !== null && b === null) comparison = 1;
      else if (typeof a === 'number' && typeof b === 'number') comparison = a - b;
      else if (a !== null && b !== null) comparison = String(a).localeCompare(String(b), 'en', { sensitivity: 'base' });
      if (direction === 'desc') comparison = -comparison;
      return comparison || left.orderMstrId - right.orderMstrId;
    });
  }

  const pageNo = Math.max(request.pageNo ?? 1, 1);
  const rowsPerPage = request.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE;
  const offset = (pageNo - 1) * rowsPerPage;

  return {
    searchRowCount: filteredOrders.length,
    readyToShipOrderCount: cachedResponse.readyToShipOrderCount,
    results: filteredOrders.slice(offset, offset + rowsPerPage),
  };
};
